/**
 * DocumentPreviewDockHost — most między flagą `previewDockEnabled` na aktywnym
 * layoucie a gotowymi `RecordDetailDock` + `DocumentPreviewPanel` (Fala B).
 * Layout CAŁEJ strony (dzieli ją na formularz + boczny dokowany panel podglądu
 * załączników), więc — w odróżnieniu od `document_preview_view` (element
 * WEWNĄTRZ formularza, dispatchowany przez `contextRenderers`) — owija całe
 * drzewo JSX strony, nie tylko jeden slot.
 *
 * Wpięty na 9 stronach `*Detail`: UserDetail, EmployeeDetail, ContractorDetail,
 * ContractDetail, LetterDetail, CommercialDocDetail, InternalDocDetail,
 * SaleInvoiceDetail, PurchaseInvoiceDetail. Każda owija swoje istniejące
 * drzewo JSX tym komponentem, np.:
 *
 *   <DocumentPreviewDockHost
 *     enabled={previewDockEnabled && !!id}
 *     moduleId="users"
 *     ownerType="user"
 *     ownerId={id}
 *   >
 *     {existing page JSX (nagłówek, zakładki, formularz...)}
 *   </DocumentPreviewDockHost>
 *
 * `enabled` = `previewDockEnabled` z aktywnego layoutu (`useSystemModuleActiveLayout`,
 * modelowane jak `isDefault` — patrz PUT [layoutId]/route.ts) ORAZ rekord zapisany
 * (`ownerId` obecny — tryb create nie ma jeszcze załączników).
 *
 * `enabled=false` → renderuje WYŁĄCZNIE `children`, zero zmiany layoutu dla stron
 * z wyłączonym dokiem. Zawsze pokazuje WSZYSTKIE załączniki rekordu (`source: 'all'`)
 * — dok to trwały podgląd boczny, nie miejsce na filtrowanie do jednego
 * głównego/najnowszego pliku (to rola inline `document_preview_view`, patrz
 * `DocumentPreviewContextRenderer.tsx`).
 */

import { type ReactNode, useState } from 'react';
import type { AttachmentOwnerType } from '@saas/shared';

import { DocumentPreviewPanel } from './DocumentPreviewPanel';
import { RecordDetailDock } from './RecordDetailDock';
import { useResolvedPreviewAttachments } from './use-resolved-preview-attachments';

export interface DocumentPreviewDockHostProps {
  /** `true` gdy strona ma włączony dok ORAZ rekord jest zapisany (patrz nagłówek). */
  enabled: boolean;
  /** Klucz localStorage szerokości panelu (`RecordDetailDock.moduleId`) — zwykle slug modułu. */
  moduleId: string;
  ownerType: AttachmentOwnerType;
  ownerId: string | null | undefined;
  companyId?: string;
  children: ReactNode;
}

export function DocumentPreviewDockHost({
  enabled,
  moduleId,
  ownerType,
  ownerId,
  companyId,
  children,
}: DocumentPreviewDockHostProps) {
  // Hooki wywoływane bezwarunkowo (reguła React) — fetch sam gasi się przez
  // `enabled` gdy dok jest wyłączony, więc nieaktywne strony nie płacą za sieć.
  const [activeId, setActiveId] = useState<string | null>(null);
  const { attachments } = useResolvedPreviewAttachments({
    ownerType,
    ownerId: ownerId ?? '',
    companyId,
    source: 'all',
    fieldSlug: null,
    enabled: enabled && !!ownerId,
  });

  if (!enabled) return <>{children}</>;

  const activeAttachmentId =
    activeId && attachments.some((a) => a.id === activeId)
      ? activeId
      : (attachments[0]?.id ?? null);

  return (
    <RecordDetailDock
      moduleId={moduleId}
      panelContent={({ onCollapse }) => (
        <DocumentPreviewPanel
          attachments={attachments}
          activeAttachmentId={activeAttachmentId}
          onActiveAttachmentChange={setActiveId}
          mode="dock"
          onCollapse={onCollapse}
        />
      )}
    >
      {children}
    </RecordDetailDock>
  );
}

export default DocumentPreviewDockHost;
